import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./auth";

/* 장바구니 — 결제 연동 전까지는 브라우저(localStorage)에만 보관한다.
   담을 때의 이름·가격·사진을 함께 저장해 목록 화면이 API 없이 그려지게 한다. */

const CartContext = createContext(null);
const KEY = "imeta_cart_v1";
/* 라오나봇 수동 프로젝트의 [주문하기]로 부품을 담았으면 그 프로젝트 번호 — 주문서가 함께 보내
   주문을 프로젝트에 잇는다(그래야 프로젝트 자료 다운로드가 열린다). 장바구니가 비면 함께 지운다 */
const PKEY = "imeta_cart_projects_v1";
/* 라오나봇 펌웨어 개발의뢰 — 내 프로젝트 [주문하기]에서 [펌웨어 빌드 포함]에 체크하면 담긴다.
   [{id, no, title, fee}] — 금액은 서버가 주문할 때 다시 정한다(app/laona_fw.py), 여기 값은 화면 표시용 */
const FKEY = "imeta_cart_fw_v1";

function loadFw() {
  try {
    const rows = JSON.parse(window.localStorage.getItem(FKEY) || "[]");
    return Array.isArray(rows) ? rows.filter((r) => r && Number.isInteger(r.id) && r.id > 0).slice(-20) : [];
  } catch {
    return [];
  }
}

function loadProjects() {
  try {
    const ids = JSON.parse(window.localStorage.getItem(PKEY) || "[]");
    return Array.isArray(ids) ? ids.filter((n) => Number.isInteger(n) && n > 0).slice(-20) : [];
  } catch {
    return [];
  }
}

function load() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items.filter((i) => i && i.slug && i.qty > 0) : [];
  } catch {
    return [];
  }
}

function pick(product) {
  return {
    slug: product.slug,
    name: product.name,
    price: product.price,
    price_note: product.price_note || "",
    thumb: product.thumb || (product.images && product.images[0]) || null,
    category: product.category || "",
  };
}

/* 로그인 안내 — 장바구니 담기·구매는 로그인한 회원만 할 수 있다 */
function LoginPrompt({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const go = (path) => { onClose(); navigate(path, { state: { from } }); };

  return (
    <div className="login-prompt" role="presentation" onClick={onClose}>
      <div
        className="login-prompt__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-prompt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="login-prompt-title" className="login-prompt__title">로그인이 필요합니다</h2>
        <p className="login-prompt__desc">
          장바구니 담기와 구매는 로그인 후 이용할 수 있습니다.<br />
          로그인하면 보던 화면으로 돌아옵니다.
        </p>
        <div className="login-prompt__actions">
          <button type="button" className="btn btn--primary" onClick={() => go("/login")} autoFocus>
            로그인
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => go("/signup")}>
            회원가입
          </button>
        </div>
        <button type="button" className="login-prompt__close" onClick={onClose} aria-label="닫기">×</button>
      </div>
    </div>
  );
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [projects, setProjects] = useState(loadProjects);
  const [fw, setFw] = useState(loadFw);
  const { member } = useAuth();
  const [prompt, setPrompt] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      /* 저장소를 쓸 수 없는 환경(사생활 보호 모드 등)에서는 새로고침 전까지만 유지 */
    }
  }, [items]);

  useEffect(() => {
    if (!items.length && projects.length) { setProjects([]); return; }
    try {
      window.localStorage.setItem(PKEY, JSON.stringify(projects));
    } catch {
      /* 위와 같다 */
    }
  }, [items, projects]);

  useEffect(() => {
    try {
      window.localStorage.setItem(FKEY, JSON.stringify(fw));
    } catch {
      /* 위와 같다 */
    }
  }, [fw]);

  /* 다른 탭에서 바꾼 장바구니를 따라간다 */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setItems(load());
      if (e.key === PKEY) setProjects(loadProjects());
      if (e.key === FKEY) setFw(loadFw());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) {
        return prev.map((i) => (i.slug === product.slug ? { ...i, ...pick(product), qty: i.qty + qty } : i));
      }
      return [...prev, { ...pick(product), qty }];
    });
  }, []);

  const setQty = useCallback((slug, qty) => {
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: Math.max(1, Math.min(999, qty)) } : i)));
  }, []);

  const remove = useCallback((slug) => {
    setItems((prev) => prev.filter((i) => i.slug !== slug));
  }, []);

  const clear = useCallback(() => { setItems([]); setFw([]); }, []);

  const addProject = useCallback((id) => {
    setProjects((prev) => (prev.includes(id) ? prev : [...prev, id].slice(-20)));
  }, []);

  const addFw = useCallback((row) => {
    setFw((prev) => [...prev.filter((r) => r.id !== row.id), row].slice(-20));
  }, []);

  const removeFw = useCallback((id) => {
    setFw((prev) => prev.filter((r) => r.id !== id));
  }, []);

  /* 로그인했으면 action 을 실행하고, 아니면 로그인 안내를 띄운다 */
  const requireLogin = useCallback((action) => {
    if (member) {
      action();
      return true;
    }
    setPrompt(true);
    return false;
  }, [member]);

  const value = useMemo(() => ({
    items,
    requireLogin,
    askLogin: () => setPrompt(true),
    /* 장바구니 배지 숫자 — 수량 합이 아니라 담긴 상품 종류 수 */
    count: items.length + fw.length,
    total: items.reduce((n, i) => n + (i.price || 0) * i.qty, 0),
    fw,
    fwTotal: fw.reduce((n, r) => n + (r.fee || 0), 0),
    addFw,
    removeFw,
    add,
    setQty,
    remove,
    clear,
    projects,
    addProject,
  }), [items, add, setQty, remove, clear, projects, addProject, fw, addFw, removeFw, requireLogin]);

  return (
    <CartContext.Provider value={value}>
      {children}
      {prompt && <LoginPrompt onClose={() => setPrompt(false)} />}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart 는 CartProvider 안에서만 쓸 수 있습니다.");
  return ctx;
}
